import { PartialBlock } from "@blocknote/core";

/**
 * The `t` from `useTranslation()`, narrowed to what this module calls.
 *
 * Passed in rather than imported: these are module-level helpers, and a `t`
 * resolved at import time would freeze the section headings at the language the
 * app started in.
 */
export type BriefingTranslator = (key: string, options?: Record<string, unknown>) => string;

export type OldFormatBriefData = {
  Attendees: Array<{
    postgres_id: number | null;
    summary: string;
  }>;
  Companies: Array<{
    postgres_id: number;
    Company: string;
    Description?: string | null;
  }>;
  EngagementHistory?: {
    Summary?: string;
    ActionItems?: string[];
  };
  lastActivity?: string;
  lastActivityId?: string;
  nextStep?: string;
  nextStepId?: string;
};

export type NewFormatBriefData = {
  Attendees: Record<string, Array<{
    postgres_id?: number;
    name?: string;
    title?: string;
    linkedin?: string;
    location?: string;
  }>>;
  Companies?: Array<{
    postgres_id?: number;
    name?: string;
    domain?: string;
    description?: string;
    updates?: string[];
  }>;
  EngagementHistory?: {
    Summary?: string;
    ActionItems?: string[];
  };
  lastActivity?: string;
  lastActivityId?: string;
  nextStep?: string;
  nextStepId?: string;
  [sectionName: string]: any; // Dynamic sections from custom templates
};

export function isNewFormat(brief: any): brief is NewFormatBriefData {
  return (
    "Attendees" in brief &&
    typeof brief.Attendees === "object" &&
    !Array.isArray(brief.Attendees)
  );
}

/**
 * Render an AI brief as editor blocks.
 *
 * Only the SECTION HEADINGS are Coffee's own words and translated. Everything
 * else — attendee lines, company names and descriptions, the summary, action
 * items, and the dynamic section names a custom briefing template defines — is
 * content the backend produced, and is passed through untouched.
 */
export function briefToBlocks(
  brief: OldFormatBriefData | NewFormatBriefData,
  t: BriefingTranslator,
): PartialBlock[] {
  if (isNewFormat(brief)) {
    return newFormatBriefToBlocks(brief, t);
  }
  return oldFormatBriefToBlocks(brief, t);
}

function oldFormatBriefToBlocks(brief: OldFormatBriefData, t: BriefingTranslator): PartialBlock[] {
  const blocks: PartialBlock[] = [];

  // Attendees Section
  if (brief.Attendees && brief.Attendees.length > 0) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: t("meetingBriefing.attendees"), styles: {} }],
    });

    brief.Attendees.forEach((attendee) => {
      if (attendee.summary) {
        blocks.push({
          type: "bulletListItem",
          content: [{ type: "text" as const, text: attendee.summary, styles: {} }],
        });
      }
    });

    blocks.push({ type: "paragraph", content: [] }); // spacing
  }

  // Companies Section
  if (brief.Companies && brief.Companies.length > 0) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [
        {
          type: "text",
          text: t("meetingBriefing.companies", { count: brief.Companies.length }),
          styles: {},
        },
      ],
    });

    brief.Companies.forEach((company) => {
      const content: any[] = [];

      // Add company name
      content.push({
        type: "text" as const,
        text: company.Company,
        styles: {},
      });

      // Add description if exists
      if (company.Description) {
        content.push({
          type: "text" as const,
          text: `: ${company.Description}`,
          styles: {},
        });
      }

      blocks.push({
        type: "bulletListItem",
        content: content,
      });
    });

    blocks.push({ type: "paragraph", content: [] }); // spacing
  }

  // Engagement History Section
  if (brief.EngagementHistory?.Summary) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: t("meetingBriefing.activityHistory"), styles: {} }],
    });

    blocks.push({
      type: "paragraph",
      content: [
        {
          type: "text" as const,
          text: brief.EngagementHistory.Summary,
          styles: {},
        },
      ],
    });

    // Action Items sub-section
    if (brief.EngagementHistory.ActionItems && brief.EngagementHistory.ActionItems.length > 0) {
      blocks.push({
        type: "heading",
        props: { level: 3 },
        content: [{ type: "text" as const, text: t("meetingBriefing.actionItems"), styles: {} }],
      });

      brief.EngagementHistory.ActionItems.forEach((item) => {
        blocks.push({
          type: "bulletListItem",
          content: [{ type: "text" as const, text: item, styles: {} }],
        });
      });
    }

    blocks.push({ type: "paragraph", content: [] }); // spacing
  }

  // Last Activity Section
  if (brief.lastActivity) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: t("meetingBriefing.lastActivity"), styles: {} }],
    });

    blocks.push({
      type: "paragraph",
      content: [{ type: "text" as const, text: brief.lastActivity, styles: {} }],
    });

    blocks.push({ type: "paragraph", content: [] }); // spacing
  }

  // Next Step Section
  if (brief.nextStep) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: t("meetingBriefing.nextStep"), styles: {} }],
    });

    blocks.push({
      type: "paragraph",
      content: [{ type: "text" as const, text: brief.nextStep, styles: {} }],
    });
  }

  return blocks;
}

function newFormatBriefToBlocks(brief: NewFormatBriefData, t: BriefingTranslator): PartialBlock[] {
  const blocks: PartialBlock[] = [];

  // Attendees Section (grouped by company)
  if (brief.Attendees && Object.keys(brief.Attendees).length > 0) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: t("meetingBriefing.attendees"), styles: {} }],
    });

    Object.entries(brief.Attendees).forEach(([companyName, attendees]) => {
      const attendeeBlocks = attendees
        .filter((attendee) => attendee.name || attendee.postgres_id)
        .map((attendee) => {
          const content: any[] = [];

          const name = attendee.name ?? "";

          if (name) {
            content.push({
              type: "text" as const,
              text: name,
              styles: {},
            });
          }

          if (attendee.title) {
            content.push({
              type: "text" as const,
              text: `, ${attendee.title}`,
              styles: {},
            });
          }

          if (attendee.location) {
            content.push({
              type: "text" as const,
              text: ` (${attendee.location})`,
              styles: {},
            });
          }

          return {
            type: "bulletListItem" as const,
            content: content,
          };
        });

      // Skip company groups with no visible attendees
      if (attendeeBlocks.length === 0) return;

      blocks.push({
        type: "bulletListItem",
        content: [
          {
            type: "text" as const,
            text: companyName,
            styles: { bold: true },
          },
        ],
        children: attendeeBlocks,
      });
    });

    blocks.push({ type: "paragraph", content: [] }); // spacing
  }

  // Companies Section
  if (brief.Companies && brief.Companies.length > 0) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [
        {
          type: "text",
          text: t("meetingBriefing.companies", { count: brief.Companies.length }),
          styles: {},
        },
      ],
    });

    brief.Companies.forEach((company) => {
      const content: any[] = [];

      // Ensure name is always a string (never null/undefined)
      const companyName = company.name ?? "";

      if (companyName) {
        content.push({
          type: "text" as const,
          text: companyName,
          styles: { bold: true },
        });
      }

      if (company.domain) {
        content.push({ type: "text" as const, text: ` • `, styles: {} });
        content.push({
          type: "text" as const,
          text: company.domain,
          styles: {},
        });
      }

      // Create children blocks for description and updates
      const childrenBlocks: any[] = [];

      if (company.description) {
        childrenBlocks.push({
          type: "bulletListItem" as const,
          content: [
            { type: "text" as const, text: company.description, styles: {} },
          ],
        });
      }

      if (company.updates && company.updates.length > 0) {
        company.updates.forEach((update) => {
          childrenBlocks.push({
            type: "bulletListItem" as const,
            content: [{ type: "text" as const, text: update, styles: {} }],
          });
        });
      }

      blocks.push({
        type: "bulletListItem",
        content: content,
        children: childrenBlocks.length > 0 ? childrenBlocks : undefined,
      });
    });

    blocks.push({ type: "paragraph", content: [] }); // spacing
  }

  // Engagement History Section (same as old format)
  if (brief.EngagementHistory?.Summary) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: t("meetingBriefing.activityHistory"), styles: {} }],
    });

    blocks.push({
      type: "paragraph",
      content: [
        {
          type: "text" as const,
          text: brief.EngagementHistory.Summary,
          styles: {},
        },
      ],
    });

    if (brief.EngagementHistory.ActionItems && brief.EngagementHistory.ActionItems.length > 0) {
      blocks.push({
        type: "heading",
        props: { level: 3 },
        content: [{ type: "text" as const, text: t("meetingBriefing.actionItems"), styles: {} }],
      });

      brief.EngagementHistory.ActionItems.forEach((item) => {
        blocks.push({
          type: "bulletListItem",
          content: [{ type: "text" as const, text: item, styles: {} }],
        });
      });
    }

    blocks.push({ type: "paragraph", content: [] }); // spacing
  }

  // Last Activity and Next Step (same as old format)
  if (brief.lastActivity) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: t("meetingBriefing.lastActivity"), styles: {} }],
    });

    blocks.push({
      type: "paragraph",
      content: [{ type: "text" as const, text: brief.lastActivity, styles: {} }],
    });

    blocks.push({ type: "paragraph", content: [] }); // spacing
  }

  if (brief.nextStep) {
    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: t("meetingBriefing.nextStep"), styles: {} }],
    });

    blocks.push({
      type: "paragraph",
      content: [{ type: "text" as const, text: brief.nextStep, styles: {} }],
    });

    blocks.push({ type: "paragraph", content: [] }); // spacing
  }

  // Dynamic sections (including Background and custom template sections)
  // Process any other sections that are arrays of strings
  Object.keys(brief).forEach((sectionName) => {
    // Skip the core sections we already processed
    if (
      sectionName === 'Attendees' ||
      sectionName === 'Companies' ||
      sectionName === 'EngagementHistory' ||
      sectionName === 'lastActivity' ||
      sectionName === 'lastActivityId' ||
      sectionName === 'nextStep' ||
      sectionName === 'nextStepId'
    ) {
      return;
    }

    const sectionData = (brief as any)[sectionName];

    // Check if it's an array of strings (typical for custom sections)
    if (Array.isArray(sectionData) && sectionData.length > 0) {
      blocks.push({
        type: "heading",
        props: { level: 3 },
        content: [{ type: "text" as const, text: sectionName, styles: {} }],
      });

      sectionData.forEach((item) => {
        // Handle both string items and potentially structured items
        const text = typeof item === 'string' ? item : JSON.stringify(item);
        blocks.push({
          type: "bulletListItem",
          content: [{ type: "text" as const, text: text, styles: {} }],
        });
      });

      blocks.push({ type: "paragraph", content: [] }); // spacing
    }
  });

  return blocks;
}
